import moment from "moment-timezone";
import { useIntl } from "react-intl";
import ISupplier_Delivery_Schedule from "../../../models/ISupplier_Delivery_Schedule";
import {
    DAY_ABBR_KEYS,
    DEFAULT_DELIVERY_TIMEZONE,
    format12h,
    getNextDelivery,
    sortDays,
} from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";
import "./DeliveryDeadlineInfo.scss";

type Props = {
    schedule?: ISupplier_Delivery_Schedule | null;
    timezone?: string | null;
};

/**
 * Read-only summary of a vendor's delivery schedule for the order/checkout Ship To
 * area: the configured delivery windows, the next expected delivery date, and the
 * current hard deadline. Date math runs in the vendor/supplier timezone (falls back to
 * DEFAULT_DELIVERY_TIMEZONE, same as the API). Renders nothing when the vendor has no schedule days.
 */
export const DeliveryDeadlineInfo = (props: Props) => {
    const intl = useIntl();

    const days = sortDays(props.schedule?.Days);
    if (!days.length) {
        return null;
    }

    const tz = props.timezone || DEFAULT_DELIVERY_TIMEZONE;
    const next = getNextDelivery(props.schedule, tz);

    // All configured delivery days, e.g. "Sun, Wed".
    const windows = days.map((d) => intl.formatMessage({ id: DAY_ABBR_KEYS[d.delivery_day_of_week] })).join(", ");

    const deliveryText = next ? next.deliveryDate.format("ddd, MMM D") : intl.formatMessage({ id: "delivery_deadline_passed" });

    // Zone abbreviation (e.g. CDT) so buyers in other timezones read the cutoff correctly.
    const deadlineText = next
        ? `${next.hardDeadline.format("ddd, MMM D")} ${format12h(next.day.hard_deadline_time)} ${next.hardDeadline.format("z")}`
        : "—";

    // Flag deadlines that are within a day so staff/buyers can act now.
    const isUrgent = !!next && next.hardDeadline.diff(moment.tz(tz), "hours") <= 24;

    return (
        <div className="DeliveryDeadlineInfo rounded p-2 mb-2 small">
            <div className="fw-bold mb-1">
                <i className="fas fa-truck me-1" />
                {intl.formatMessage({ id: "delivery_schedule" })}
            </div>
            <div className="d-flex flex-column">
                <div>
                    <span className="fw-bold">{intl.formatMessage({ id: "next_expected_delivery" })}: </span>
                    <span>{deliveryText}</span>
                </div>
                <div className={isUrgent ? "delivery-deadline-urgent fw-bold" : ""}>
                    <span className="fw-bold">{intl.formatMessage({ id: "hard_deadline" })}: </span>
                    <span>{deadlineText}</span>
                </div>
                <div>
                    <span className="fw-bold">{intl.formatMessage({ id: "delivery_windows" })}: </span>
                    <span>{windows}</span>
                </div>
            </div>
        </div>
    );
};

export default DeliveryDeadlineInfo;
